import { MonthlyImpactMap, UpdateStatus } from "aint-types";
import { getMonthAndYear } from "aint-utils";
import dayjs, { Dayjs } from "dayjs";

export function getConfirmationTypesForImpactRange({
  impactRange,
  impactSeries,
  confirmationDate,
  startDate,
}: {
  impactRange: { year: string; months: string[] }[];
  impactSeries: MonthlyImpactMap;
  confirmationDate?: Dayjs;
  startDate?: Dayjs;
}) {
  // No confirmation necessary if we are creating an initiative.
  if (!startDate) {
    return undefined;
  }

  const confirmationType: Record<string, Record<string, UpdateStatus>> = {};
  const [currentMonth, currentYear] = getMonthAndYear();

  const assignConfirmationType = (
    year: string,
    month: string,
    type: UpdateStatus
  ) => {
    if (!confirmationType[year]) {
      confirmationType[year] = {};
    }

    confirmationType[year][month] = type;
  };

  impactRange.forEach(({ year, months }) => {
    months.forEach((month) => {
      const fieldDate = dayjs(`${year}-${month + 1}-1`, "Europe/Berlin");
      const fieldValue = impactSeries?.[year]?.[month];

      const needsConfirmation =
        //if they already have a value
        !!fieldValue &&
        fieldValue.length > 0 &&
        // are not modified
        // and the confirmation date is before the field date.
        (confirmationDate?.isBefore(fieldDate) || !confirmationDate);

      const monthDifferenceToConfirmation = Math.round(
        fieldDate?.diff(confirmationDate, "month", true)
      );

      // if the confirmation Date is not set yet, we treat all fields up to the current month's field as "overdue"
      // and the current month's field as "pending"
      if (confirmationDate === undefined) {
        const fieldDateDifference = Math.round(
          fieldDate.diff(dayjs().set("date", 1), "month", true)
        );
        if (fieldDateDifference === 0) {
          assignConfirmationType(year, month, "pending");
        } else if (fieldDateDifference < 0) {
          assignConfirmationType(year, month, "overdue");
        }
        // if the confirmation date is set, we use the difference between the field date and the confirmation date, to
        // determine if the confirmation type should be "overdue" or "pending"
      } else if (needsConfirmation) {
        if (
          monthDifferenceToConfirmation > 0 &&
          +month === currentMonth &&
          year === currentYear
        ) {
          assignConfirmationType(year, month, "pending");
        } else if (monthDifferenceToConfirmation > 0) {
          assignConfirmationType(year, month, "overdue");
        }
      } else {
        assignConfirmationType(year, month, "completed");
      }
    });
  });

  return confirmationType;
}
